"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import img from "../images/homepage.gif";
import ContactModal from "./ContactModal";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.16 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function Intro() {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <section
        id="about"
        className="relative overflow-hidden
                   bg-gradient-to-b from-white via-blue-50/50 to-white
                   py-28 sm:py-32 lg:py-40 px-6 sm:px-10 lg:px-20"
      >
        {/* Decorative background */}
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px]
                        bg-blue-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[520px] h-[520px]
                        bg-blue-400/20 rounded-full blur-3xl" />

        <motion.div
          className="relative max-w-7xl mx-auto
                     grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-28 items-center"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {/* TEXT */}
          <motion.div variants={container}>
            <motion.span
              variants={fadeUp}
              className="inline-block mb-6 px-4 py-1.5 rounded-full
                         bg-blue-100 text-blue-800 text-xs font-semibold tracking-widest uppercase"
            >
              {t("intro.about_title")}
            </motion.span>

            <motion.h2
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl
                         font-extrabold text-blue-950 leading-tight mb-8"
            >
              {t("intro.company_name")}
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="text-lg sm:text-xl font-semibold
                         text-gray-900 leading-relaxed mb-10 max-w-xl"
            >
              {t("intro.main_heading")}
            </motion.p>

            <motion.div
              variants={container}
              className="space-y-6 text-gray-700 max-w-xl"
            >
              {[
                "intro.paragraph_1",
                "intro.paragraph_2",
                "intro.paragraph_3",
                "intro.paragraph_4",
              ].map((key) => (
                <motion.p
                  key={key}
                  variants={fadeUp}
                  className="text-base sm:text-lg leading-relaxed"
                >
                  {t(key)}
                </motion.p>
              ))}
            </motion.div>

            {/* Trust metrics */}
            <motion.div
              variants={fadeUp}
              className="flex flex-wrap gap-4 mt-12"
            >
              {[
                t("addons.strategy"),
                t("addons.term"),
                t("addons.solutions"),
              ].map((label, index) => (
                <span
                  key={index}
                  className="px-4 py-2 rounded-full
                            bg-white/90 backdrop-blur
                            text-sm font-semibold text-blue-900
                            shadow-md border border-blue-100
                            hover:shadow-lg hover:-translate-y-0.5
                            transition-all duration-300"
                >
                  {label}
                </span>
              ))}
            </motion.div>

            <motion.button
              variants={fadeUp}
              whileHover={{ y: -3, scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setIsModalOpen(true)}
              className="mt-14 inline-flex items-center justify-center
                         px-14 py-4 rounded-full
                         bg-blue-700 text-white text-sm font-semibold
                         shadow-xl shadow-blue-700/30
                         hover:bg-blue-800 hover:shadow-2xl
                         transition-all duration-300"
            >
              {t("intro.contact_btn")}
            </motion.button>
          </motion.div>

          {/* IMAGE */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 40 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
              },
            }}
            className="relative flex justify-center"
          >
            <motion.div
              whileHover={{ y: -12, scale: 1.05 }}
              transition={{ type: "spring", stiffness: 130 }}
              className="relative w-[360px] sm:w-[480px] lg:w-[560px]"
            >
              {/* Ultra-bold glow */}
              <div className="absolute -inset-10
                              bg-gradient-to-tr from-blue-500/40 via-blue-400/30 to-blue-300/40
                              blur-[90px] rounded-[3rem]" />

              {/* Frame */}
              <div className="absolute inset-0 rounded-[2.5rem]
                              ring-2 ring-blue-700/50 ring-offset-8 ring-offset-white" />

              {/* Image card */}
              <div className="relative overflow-hidden rounded-[2.5rem]
                              bg-white shadow-2xl">
                <img
                  src={img}
                  alt="Lak-Che"
                  className="w-full h-full object-cover
                             contrast-125 saturate-125"
                />

                <div className="absolute inset-0 bg-gradient-to-t
                                from-blue-950/30 via-transparent to-transparent" />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
